import React, { FC, useCallback, useEffect } from 'react';
import {
  Control,
  useFieldArray,
  UseFormClearErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormInput } from '../../../../components/input/index';
import { useLocale } from '../../../../hooks/index';
import { EventFormType } from '../../../../types/events.types';
import { toNumber } from '../../../../utils/to-number.util';
import { isTerminatedStakeholderIssuanceRowFrozen } from '../../utils/terminated-stakeholder.util';
import {
  FormDropDown,
  IssuanceRowStatus,
  ReadOnlyField,
  ReverseVestingFields,
  ShareClassCombobox,
  StakeholderCombobox,
} from '../components/index';
import { FormSchema, shareIssuanceItemSchema } from '../event-form.validation';
import {
  isReverseVestingIncomplete,
  resetReverseVestingOnStakeholderChange,
  resetReverseVestingSharesOnIssuedSharesChange,
} from '../utils/reverse-vesting-form.util';

export type ShareIssuanceOptionalStepProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  /** Edit mode — persisted rows of terminated stakeholders freeze into read-only cards. */
  lockMode?: boolean;
  setFormData: UseFormSetValue<FormSchema>;
};

export const ShareIssuanceOptionalStep: FC<ShareIssuanceOptionalStepProps> = ({
  control,
  companyId,
  clearErrors,
  lockMode,
  setFormData,
}) => {
  const { initialStep, optionalStep } = useWatch<FormSchema>({ control });
  const { messagesLocale } = useLocale();
  const {
    fields: issuanceItemsFields,
    append: issuanceItemsAppend,
    remove: issuanceItemsRemove,
  } = useFieldArray({
    control,
    name: 'optionalStep.share-issuance.issuanceItems',
  });

  const handleRemoveIssuanceItems = useCallback(
    (index: number) => {
      issuanceItemsRemove(index);
    },
    [issuanceItemsRemove],
  );

  const handleAppendIssuanceItems = useCallback(() => {
    issuanceItemsAppend({
      shareClass: { id: '', name: '' },
      stakeholder: { id: '', fullName: '' },
      shares: 0,
      reverseVesting: undefined,
    });
  }, [issuanceItemsAppend]);

  const getFullTitle = (index: number) => {
    const stakeholderName =
      optionalStep?.['share-issuance']?.issuanceItems?.[index]?.stakeholder?.fullName;

    if (stakeholderName) {
      return `${messagesLocale['ISSUANCE']} - ${stakeholderName}`;
    }

    return messagesLocale['ISSUANCE'];
  };

  /**
   * The row's own schema decides completeness rather than `formState.errors`: errors only populate
   * for fields the user has touched, so an untouched blank row would otherwise read as complete.
   */
  const getRowStatus = (index: number) => {
    const row = optionalStep?.['share-issuance']?.issuanceItems?.[index];

    return {
      isComplete: shareIssuanceItemSchema.safeParse(row).success,
      isReverseVestingEnabled: Boolean(row?.reverseVesting?.enabled),
      isReverseVestingIncomplete: isReverseVestingIncomplete(row),
      stakeholderName: row?.stakeholder?.fullName || messagesLocale['ISSUANCE'],
    };
  };

  useEffect(() => {
    if (initialStep?.type === EventFormType.SHARE_ISSUANCE && issuanceItemsFields.length === 0) {
      handleAppendIssuanceItems();
    }
  }, [handleAppendIssuanceItems, initialStep?.type, issuanceItemsFields.length]);

  return (
    <div className="flex flex-col gap-6 px-6">
      {issuanceItemsFields.map((field, index) => {
        const row = optionalStep?.['share-issuance']?.issuanceItems?.[index];
        // Termination settled this row's holdings, so the whole card — details and schedule —
        // is frozen in edit mode. New rows (no id yet) stay editable even for terminated holders.
        const isRowFrozen =
          Boolean(lockMode) && isTerminatedStakeholderIssuanceRowFrozen(row ?? {});

        return (
          <FormDropDown
            handleRemove={() => handleRemoveIssuanceItems(index)}
            key={index}
            showRemoveButton={issuanceItemsFields.length > 1 && !isRowFrozen}
            title={getFullTitle(index)}
            titleSlot={<IssuanceRowStatus {...getRowStatus(index)} />}
          >
            <div className="flex w-full flex-col gap-3" key={field.id}>
              {isRowFrozen ? (
                <div className="grid w-full grid-cols-2 gap-3">
                  <ReadOnlyField
                    label={<AppFormattedMessage id="STAKEHOLDER" />}
                    value={row?.stakeholder?.fullName}
                  />
                  <ReadOnlyField
                    label={<AppFormattedMessage id="SHARE_CLASS" />}
                    value={row?.shareClass?.name}
                  />
                  <ReadOnlyField
                    label={<AppFormattedMessage id="NO_OF_SHARES" />}
                    value={Number(row?.shares ?? 0).toLocaleString('en-US')}
                  />
                </div>
              ) : (
                <>
                  <div className="flex w-full items-center justify-between">
                    <span className="text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id="STAKEHOLDER" />
                    </span>
                    <StakeholderCombobox
                      companyId={companyId}
                      control={control}
                      name={`optionalStep.share-issuance.issuanceItems.${index}.stakeholder`}
                      onSelectStakeholder={({ id }) =>
                        resetReverseVestingOnStakeholderChange({
                          clearErrors,
                          currentStakeholderId:
                            optionalStep?.['share-issuance']?.issuanceItems?.[index]?.stakeholder
                              ?.id,
                          namePrefix: `optionalStep.share-issuance.issuanceItems.${index}.reverseVesting`,
                          nextStakeholderId: id,
                          setFormData,
                        })
                      }
                    />
                  </div>
                  <div className="flex w-full items-center justify-between">
                    <span className="text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id="SHARE_CLASS" />
                    </span>
                    <ShareClassCombobox
                      companyId={companyId}
                      control={control}
                      fieldName={`optionalStep.share-issuance.issuanceItems.${index}.shareClass`}
                    />
                  </div>
                  <div className="flex w-full items-center justify-between">
                    <span className="text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id="NO_OF_SHARES" />
                    </span>
                    <FormInput
                      control={control}
                      name={`optionalStep.share-issuance.issuanceItems.${index}.shares`}
                      numberOnly
                      onChange={(value) =>
                        resetReverseVestingSharesOnIssuedSharesChange({
                          clearErrors,
                          currentIssuedShares: Number(row?.shares ?? 0),
                          namePrefix: `optionalStep.share-issuance.issuanceItems.${index}.reverseVesting`,
                          nextIssuedShares: toNumber(value),
                          reverseVesting: row?.reverseVesting,
                          setFormData,
                        })
                      }
                      placeholder={<AppFormattedMessage id="VALUE" />}
                      shouldFormatNumber
                      wrapperClassName="w-[224px]"
                    />
                  </div>
                </>
              )}
              <ReverseVestingFields
                clearErrors={clearErrors}
                control={control}
                disabled={Boolean(row?.stakeholder?.isTerminated)}
                disabledReason={messagesLocale['REVERSE_VESTING_TERMINATED_STAKEHOLDER']}
                issuedShares={Number(row?.shares ?? 0)}
                namePrefix={`optionalStep.share-issuance.issuanceItems.${index}.reverseVesting`}
                setFormData={setFormData}
              />
            </div>
          </FormDropDown>
        );
      })}
      <Button
        className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
        disabled={issuanceItemsFields.length >= 100}
        onClick={handleAppendIssuanceItems}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_SHARE_ISSUANCE" />
      </Button>
    </div>
  );
};
